import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../../components/ui/dialog';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { matchesApi } from '../../lib/api';
import { toast } from 'sonner';
import { io } from 'socket.io-client';
import { playNotificationSound } from '../../lib/notificationSound';
import { usePresence } from '../../contexts/PresenceContext';
import { MessageSquare, Send, Shield, Calendar, Clock } from 'lucide-react';

const formatTime = (iso) => {
  if (!iso) return '';
  try {
    return new Date(iso).toLocaleTimeString('it-IT', { hour: '2-digit', minute: '2-digit' });
  } catch { return ''; }
};

const formatDate = (iso) => {
  if (!iso) return '';
  try {
    return new Date(iso).toLocaleDateString('it-IT', { day: '2-digit', month: 'short' });
  } catch { return ''; }
};

const MatchChatDialog = ({ open, onOpenChange, matchId, isItalian, currentUserId }) => {
  const [chat, setChat] = useState(null);
  const [loading, setLoading] = useState(false);
  const [sending, setSending] = useState(false);
  const [draft, setDraft] = useState('');
  const [typingUsers, setTypingUsers] = useState({}); // { user_id: gamertag }
  const scrollRef = useRef(null);
  const pollRef = useRef(null);
  const typingTimeoutRef = useRef(null);
  const otherTypingTimeoutsRef = useRef({});
  const localTypingActiveRef = useRef(false);
  const { emitTyping, onTyping, joinRoom, leaveRoom } = usePresence();

  const scrollToBottom = useCallback(() => {
    requestAnimationFrame(() => {
      const el = scrollRef.current;
      if (el) el.scrollTop = el.scrollHeight;
    });
  }, []);

  const fetchChat = useCallback(async (silent = false) => {
    if (!matchId) return;
    if (!silent) setLoading(true);
    try {
      const res = await matchesApi.getMatchChat(matchId);
      setChat(prev => {
        const newCount = res.data?.messages?.length || 0;
        const oldCount = prev?.messages?.length || 0;
        const next = res.data;
        if (newCount > oldCount) {
          // New messages: scroll
          setTimeout(scrollToBottom, 50);
        }
        return next;
      });
      // Mark all messages as read on dialog open / refresh, then notify the
      // global unread badge hook so it refreshes immediately (no waiting for
      // the 60s safety poll). Without this the badge keeps showing stale unread.
      try {
        await matchesApi.markMatchChatRead(matchId);
        window.dispatchEvent(new CustomEvent('match_chat_read', { detail: { match_id: matchId } }));
      } catch { /* ignore */ }
    } catch (err) {
      const status = err.response?.status;
      if (status === 403) {
        toast.error(isItalian ? 'Non hai accesso a questa chat' : "You don't have access to this chat");
        onOpenChange(false);
      } else if (!silent) {
        toast.error(err.response?.data?.detail || (isItalian ? 'Errore' : 'Error'));
      }
    } finally {
      if (!silent) setLoading(false);
    }
  }, [matchId, isItalian, onOpenChange, scrollToBottom]);

  useEffect(() => {
    if (!open || !matchId) return;
    fetchChat(false);

    // Real-time: connect socket and join the match-specific room
    const BACKEND_URL = window.location.origin;
    const socket = io(BACKEND_URL, {
      path: '/api/sio/',
      transports: ['polling', 'websocket'],
      reconnection: true,
      reconnectionDelay: 1000,
    });
    pollRef.current = socket;

    socket.on('connect', () => {
      socket.emit('join_room', { room: `match_chat_${matchId}` });
    });

    socket.on('match_chat_message', (msg) => {
      if (msg?.match_id !== matchId) return;
      // Don't replay sound for own messages
      const isOwn = msg?.sender_id && msg.sender_id === currentUserId;
      setChat(prev => {
        if (!prev) return prev;
        // Dedup
        if ((prev.messages || []).some(m => m.message_id === msg.message_id)) return prev;
        return { ...prev, messages: [...(prev.messages || []), msg] };
      });
      // Mark as read since dialog is open
      try {
        matchesApi.markMatchChatRead(matchId);
        window.dispatchEvent(new CustomEvent('match_chat_read', { detail: { match_id: matchId } }));
      } catch { /* ignore */ }
      if (!isOwn) playNotificationSound({ pitch: 'low' });
      setTimeout(scrollToBottom, 50);
    });

    return () => {
      try { socket.disconnect(); } catch { /* ignore */ }
      pollRef.current = null;
    };
  }, [open, matchId, fetchChat, scrollToBottom]);

  // Listen for typing events from other captains in this match chat
  useEffect(() => {
    if (!open || !matchId) return;
    const room = `match_chat_${matchId}`;
    // Make sure the presence (global) socket is in the room so typing
    // broadcasts from the backend actually reach this listener.
    joinRoom(room);
    const off = onTyping((payload) => {
      if (payload?.room !== room) return;
      if (payload?.user_id === currentUserId) return;
      const uid = payload.user_id;
      if (!uid) return;
      if (payload?.state === 'start') {
        setTypingUsers(prev => ({ ...prev, [uid]: payload.gamertag || 'Capitano' }));
        if (otherTypingTimeoutsRef.current[uid]) clearTimeout(otherTypingTimeoutsRef.current[uid]);
        otherTypingTimeoutsRef.current[uid] = setTimeout(() => {
          setTypingUsers(prev => {
            const next = { ...prev };
            delete next[uid];
            return next;
          });
          delete otherTypingTimeoutsRef.current[uid];
        }, 4000);
      } else {
        setTypingUsers(prev => {
          const next = { ...prev };
          delete next[uid];
          return next;
        });
        if (otherTypingTimeoutsRef.current[uid]) {
          clearTimeout(otherTypingTimeoutsRef.current[uid]);
          delete otherTypingTimeoutsRef.current[uid];
        }
      }
    });
    return () => {
      off();
      leaveRoom(room);
      Object.values(otherTypingTimeoutsRef.current).forEach(clearTimeout);
      otherTypingTimeoutsRef.current = {};
      setTypingUsers({});
    };
  }, [open, matchId, currentUserId, onTyping, joinRoom, leaveRoom]);

  const handleDraftChange = (val) => {
    setDraft(val);
    if (!matchId) return;
    const room = `match_chat_${matchId}`;
    if (val.length > 0) {
      if (!localTypingActiveRef.current) {
        localTypingActiveRef.current = true;
        emitTyping(room, 'start');
      }
      if (typingTimeoutRef.current) clearTimeout(typingTimeoutRef.current);
      typingTimeoutRef.current = setTimeout(() => {
        localTypingActiveRef.current = false;
        emitTyping(room, 'stop');
      }, 2500);
    } else {
      if (localTypingActiveRef.current) {
        localTypingActiveRef.current = false;
        emitTyping(room, 'stop');
      }
      if (typingTimeoutRef.current) clearTimeout(typingTimeoutRef.current);
    }
  };

  useEffect(() => {
    if (open && chat && !loading) scrollToBottom();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, loading]);

  const handleSend = async () => {
    const text = draft.trim();
    if (!text || sending) return;
    setSending(true);
    try {
      const res = await matchesApi.postMatchChat(matchId, text);
      setDraft('');
      // Stop typing indicator
      if (localTypingActiveRef.current && matchId) {
        localTypingActiveRef.current = false;
        emitTyping(`match_chat_${matchId}`, 'stop');
      }
      if (typingTimeoutRef.current) clearTimeout(typingTimeoutRef.current);
      setChat(prev => {
        if (!prev) return prev;
        // Dedup: socket may have already injected the message
        if ((prev.messages || []).some(m => m.message_id === res.data.message_id)) return prev;
        return { ...prev, messages: [...(prev.messages || []), res.data] };
      });
      setTimeout(scrollToBottom, 50);
    } catch (err) {
      toast.error(err.response?.data?.detail || (isItalian ? "Impossibile inviare il messaggio" : "Couldn't send message"));
    } finally {
      setSending(false);
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  const messages = chat?.messages || [];
  const homeName = chat?.home_club?.name || 'Casa';
  const awayName = chat?.away_club?.name || 'Ospite';
  const md = chat?.match?.match_day;
  const matchDateRaw = chat?.match?.match_date;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="bg-[#0a0d15] border-white/10 text-white p-0 overflow-hidden flex flex-col w-[calc(100vw-1rem)] max-w-2xl h-[90vh] sm:h-auto sm:max-h-[85vh] rounded-lg"
        data-testid="match-chat-dialog"
      >
        <DialogHeader className="p-3 sm:p-4 border-b border-white/10 bg-[#050508] flex-shrink-0">
          <DialogTitle className="font-heading text-sm sm:text-base uppercase tracking-wider flex items-center gap-2 pr-6 min-w-0">
            <MessageSquare size={18} className="text-[#00F0FF] flex-shrink-0" />
            <span className="truncate min-w-0 flex-1">
              {homeName} <span className="text-slate-500 mx-1">vs</span> {awayName}
            </span>
          </DialogTitle>
          <div className="flex items-center flex-wrap gap-2 sm:gap-3 text-[11px] sm:text-xs text-slate-400 mt-1">
            {md != null && (
              <span className="flex items-center gap-1">
                <Shield size={12} className="text-[#FFEA00]" />
                {isItalian ? 'Giornata' : 'Matchday'} {md}
              </span>
            )}
            {matchDateRaw && (
              <>
                <span className="flex items-center gap-1">
                  <Calendar size={12} className="text-[#0EA5E9]" />
                  {formatDate(matchDateRaw)}
                </span>
                <span className="flex items-center gap-1">
                  <Clock size={12} className="text-[#FFEA00]" />
                  {formatTime(matchDateRaw)}
                </span>
              </>
            )}
          </div>
        </DialogHeader>

        {/* Messages list */}
        <div
          ref={scrollRef}
          className="flex-1 min-h-0 overflow-y-auto px-3 sm:px-4 py-3 space-y-3 bg-[#0a0d15]"
          data-testid="match-chat-messages"
        >
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <div className="animate-spin w-6 h-6 border-2 border-[#00F0FF] border-t-transparent rounded-full" />
            </div>
          ) : messages.length === 0 ? (
            <div className="text-center text-slate-500 py-12 text-sm">
              {isItalian
                ? 'Nessun messaggio. Scrivi per primo per accordarti sulla partita!'
                : 'No messages yet. Be the first to coordinate!'}
            </div>
          ) : (
            messages.map(m => {
              const isMine = m.sender_id === currentUserId;
              const senderLabel = m.sender_gamertag || m.sender_name || '?';
              const subLabel = m.sender_club_name;
              return (
                <div
                  key={m.message_id}
                  className={`flex ${isMine ? 'justify-end' : 'justify-start'}`}
                  data-testid={`match-chat-msg-${m.message_id}`}
                >
                  <div className={`max-w-[85%] sm:max-w-[78%] rounded-lg px-3 py-2 border min-w-0 ${
                    isMine
                      ? 'bg-[#00F0FF]/10 border-[#00F0FF]/30'
                      : 'bg-[#050508] border-white/10'
                  }`}>
                    <div className="flex items-baseline gap-2 mb-1 min-w-0">
                      <span className={`text-xs font-bold truncate ${isMine ? 'text-[#00F0FF]' : 'text-[#FFEA00]'}`}>
                        {senderLabel}
                      </span>
                      {subLabel && (
                        <span className="text-[10px] text-slate-500 truncate flex-shrink min-w-0">{subLabel}</span>
                      )}
                    </div>
                    <div className="text-sm text-white whitespace-pre-wrap break-words overflow-wrap-anywhere">{m.content}</div>
                    <div className="text-[10px] text-slate-500 mt-1 text-right">{formatTime(m.created_at)}</div>
                  </div>
                </div>
              );
            })
          )}
        </div>

        {/* Composer */}
        <div className="p-3 border-t border-white/10 bg-[#050508] flex-shrink-0">
          {Object.keys(typingUsers).length > 0 && (
            <div className="text-xs text-[#00F0FF] mb-2 flex items-center gap-2" data-testid="match-typing-indicator">
              <span className="flex gap-0.5">
                <span className="w-1.5 h-1.5 rounded-full bg-[#00F0FF] animate-bounce" style={{ animationDelay: '0ms' }} />
                <span className="w-1.5 h-1.5 rounded-full bg-[#00F0FF] animate-bounce" style={{ animationDelay: '120ms' }} />
                <span className="w-1.5 h-1.5 rounded-full bg-[#00F0FF] animate-bounce" style={{ animationDelay: '240ms' }} />
              </span>
              <span>
                {Object.values(typingUsers).join(', ')}{' '}
                {isItalian
                  ? (Object.keys(typingUsers).length > 1 ? 'stanno scrivendo...' : 'sta scrivendo...')
                  : (Object.keys(typingUsers).length > 1 ? 'are typing...' : 'is typing...')}
              </span>
            </div>
          )}
          <div className="flex gap-2">
            <Input
              value={draft}
              onChange={(e) => handleDraftChange(e.target.value)}
              onKeyDown={handleKeyDown}
              placeholder={isItalian ? 'Scrivi un messaggio…' : 'Type a message…'}
              className="bg-[#0a0d15] border-white/10 text-white"
              disabled={sending || loading}
              maxLength={2000}
              data-testid="match-chat-input"
            />
            <Button
              onClick={handleSend}
              disabled={!draft.trim() || sending || loading}
              className="bg-[#00F0FF] hover:bg-[#00F0FF]/80 text-black font-bold shrink-0"
              data-testid="match-chat-send"
            >
              <Send size={16} />
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default MatchChatDialog;
